import { useEffect, useState } from "react";
import { useConfirmation } from "components/dialog/confirmation";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import toast from "react-hot-toast";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";
import ExportAgentButton from "../main/components/ExportAgentButton";
import { AGENT_TYPES } from "constants/agents";
import { cn } from "@/lib/utils";

const Setting = () => {
  const { t } = useTranslation();
  const { archiveAgent, unArchiveAgent, agentInfo, updateAgentChunkFileSize } =
    useAgentInfo();
  const { confirm } = useConfirmation();

  const [chunkFileSize, setChunkFileSize] = useState<string>(
    agentInfo?.chunk_file_size?.toString() || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const isAutonomous = agentInfo.agent_type === AGENT_TYPES.AUTONOMOUS;

  // Update chunkSize when agentInfo changes (e.g., when data is loaded)
  useEffect(() => {
    if (agentInfo?.chunk_file_size !== undefined) {
      setChunkFileSize(agentInfo?.chunk_file_size?.toString() || "");
    }
  }, [agentInfo?.chunk_file_size]);

  const handleArchive = async () => {
    const confirmed = await confirm({
      title: t("agents.settings.archiveTitle"),
      message: t("agents.settings.archiveConfirm"),
      confirmText: t("agents.settings.archive"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });
    if (confirmed) {
      try {
        await archiveAgent();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      }
    }
  };

  const handleUnarchive = async () => {
    const confirmed = await confirm({
      title: t("agents.settings.unarchiveTitle"),
      message: t("agents.settings.unarchiveConfirm"),
      confirmText: t("agents.settings.unarchive"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });
    if (confirmed) {
      try {
        await unArchiveAgent();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      }
    }
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      const chunkFileSizeValue = chunkFileSize
        ? parseInt(chunkFileSize, 10)
        : 0;

      if (
        chunkFileSize &&
        (isNaN(chunkFileSizeValue!) || chunkFileSizeValue! <= 0)
      ) {
        toast.error(t("agents.settings.chunkSizeError"));
        return;
      }

      await updateAgentChunkFileSize(chunkFileSizeValue);

      toast.success(t("agents.settings.saveSuccess"));
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-3 flex w-full flex-col gap-5 rounded-[20px]">
      {/* Agent Configuration Settings */}
      <div
        className={cn("w-full", {
          ["hidden"]: isAutonomous,
        })}
      >
        <Card extra="flex flex-col gap-6 py-6 px-4 sm:px-[30px] w-full">
          <h3 className="mb-px text-xl font-bold text-navy-700 dark:text-white">
            {t("agents.settings.chunkSize")}
          </h3>
          <InputField
            id="chunkFileSize"
            placeholder={t("agents.settings.chunkSizePlaceholder")}
            type="number"
            value={chunkFileSize}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setChunkFileSize(e.target.value)
            }
            extra="w-full"
          />
          <p className="text-sm text-gray-500">
            {t("agents.settings.chunkSizeDescription")}
          </p>

          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={isLoading}
              className="linear flex cursor-pointer items-center justify-center rounded-xl bg-brand-500 px-6 py-2 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200"
            >
              {isLoading
                ? t("common.saving")
                : t("agents.settings.saveSettings")}
            </button>
          </div>
        </Card>
      </div>

      {/* Export Agent Section */}
      <div className="w-full">
        <Card extra="flex flex-col gap-4 sm:!flex-row py-6 px-4 sm:px-[30px] justify-between items-start sm:items-center w-full">
          <div className="w-full sm:w-auto">
            <h3 className="mb-px text-xl font-bold text-navy-700 dark:text-white">
              {t("agents.settings.exportTitle")}
            </h3>
            <p className="text-base text-gray-600">
              {t("agents.settings.exportDescription")}
            </p>
          </div>
          <div className="mt-2 sm:mt-0">
            <ExportAgentButton agent={agentInfo} />
          </div>
        </Card>
      </div>

      {/* Archive/Unarchive Section */}
      <div
        className={cn("w-full", {
          ["hidden"]: isAutonomous,
        })}
      >
        {agentInfo?.is_archived ? (
          <Card extra="flex flex-col gap-4 sm:!flex-row py-6 px-4 sm:px-[30px] justify-between items-start sm:items-center w-full">
            <div className="w-full sm:w-auto">
              <h3 className="mb-px text-xl font-bold text-navy-700 dark:text-white">
                {t("agents.settings.unarchiveTitleCard")}
              </h3>
              <p className="text-base text-gray-600">
                {t("agents.settings.unarchiveDescription")}
              </p>
            </div>
            <button
              onClick={handleUnarchive}
              className="linear mt-2 flex w-full cursor-pointer items-center justify-center rounded-xl border border-red-500 px-4 py-2 text-base font-medium text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80 sm:mt-0 sm:w-auto"
            >
              {t("agents.settings.unarchiveAgent")}
            </button>
          </Card>
        ) : (
          <Card extra="flex flex-col gap-4 sm:!flex-row py-6 px-4 sm:px-[30px] justify-between items-start sm:items-center w-full">
            <div className="w-full sm:w-auto">
              <h3 className="mb-px text-xl font-bold text-navy-700 dark:text-white">
                {t("agents.settings.archiveTitleCard")}
              </h3>
              <p className="text-base text-gray-600">
                {t("agents.settings.archiveDescription")}
              </p>
            </div>
            <button
              onClick={handleArchive}
              className="linear mt-2 flex w-full cursor-pointer items-center justify-center rounded-xl border border-red-500 px-4 py-2 text-base font-medium text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80 sm:mt-0 sm:w-auto"
            >
              {t("agents.settings.archiveAgent")}
            </button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Setting;
